import type {
  CatalogHandleSearchResponse,
  CatalogProductGroup,
  CatalogSearchResponse,
  GetProductCardsResponse,
  IngestedProductSchema,
  ListCategoriesResponse,
  ListCollectionsResponse,
  ListProductCardsParams,
  ListProductCardsResponse,
  ListTagsResponse,
  ProductCard,
  ProductCardSortBy,
  ProductCardVariantCounts,
  ProductFlags,
  SearchProductsParams,
  SearchProductsResponse,
} from "@/types/ingestedProduct";

function makeProduct(
  vendor_product_id: string,
  title: string,
  brand: string,
  vendor: string,
  categories: string[],
  collections: string[],
  tags: string[],
  published_at: string | null = null,
): IngestedProductSchema {
  return {
    site_id: "mock-site",
    site: "mock-site",
    vendor_product_id,
    title,
    handle: vendor_product_id.toLowerCase(),
    url: "",
    status: "active",
    description: "",
    short_description: "",
    brand,
    vendor,
    sku: vendor_product_id,
    identifiers: {},
    ai_managed_fields: [],
    price: {
      currency: "USD",
      amount: "0.00",
      sale: "",
      cost: "0.00",
    },
    availability: { is_available: true, quantity: 0, status: "active" },
    assets: [],
    variants: [],
    categories,
    collections,
    tags,
    volume_case_pack: null,
    returnable: true,
    country_of_origin: "",
    expiration_date: null,
    boxed_length: 0,
    boxed_width: 0,
    boxed_height: 0,
    boxed_weight: 0,
    opened_length: 0,
    opened_width: 0,
    opened_height: 0,
    opened_weight: 0,
    shipping_restrictions: "",
    warranty: "",
    meta_description: "",
    meta_keywords: [],
    vendor_metadata: {},
    published_at,
    ingested_at: null,
    ingestion_source: "mock",
    ingestion_version: 1,
  };
}

const MOCK_PRODUCTS: IngestedProductSchema[] = [
  makeProduct(
    "SKU-AAA",
    "Acme Widget",
    "Acme",
    "Acme Co",
    ["Widgets"],
    ["Bestsellers"],
    ["new"],
    "2026-05-30T00:00:00",
  ),
  makeProduct(
    "SKU-BBB",
    "Beta Gadget",
    "Beta",
    "Beta Inc",
    ["Gadgets"],
    ["Featured"],
    ["sale"],
    "2026-09-10T00:00:00",
  ),
  makeProduct(
    "SKU-CCC",
    "Gamma Gizmo",
    "Gamma",
    "Gamma LLC",
    ["Gizmos"],
    ["Bestsellers"],
    ["new", "vip"],
    "2026-03-01T00:00:00",
  ),
  makeProduct(
    "SKU-DDD",
    "Delta Doohickey",
    "Delta",
    "Delta Corp",
    ["Doohickeys"],
    [],
    ["clearance"],
    "2025-11-20T00:00:00",
  ),
  makeProduct(
    "SKU-EEE",
    "Epsilon Element",
    "Epsilon",
    "Epsilon Ltd",
    ["Widgets"],
    ["Featured"],
    [],
  ),
];

const NO_FLAGS: ProductFlags = { new_since: null, price_drop: null };

/** Card-only status fields by product id; the product detail carries neither. */
const MOCK_CARD_STATUS: Record<
  string,
  Pick<ProductCard, "inventory" | "flags"> & {
    /** Sort input only: the server ranks on it but never returns it. */
    outOfStockVariants?: number;
  }
> = {
  "SKU-AAA": {
    inventory: {
      known_quantity_total: 143,
      unknown_quantity_variants: 1,
      low_stock_variants: 2,
      sellable_variants: 8,
    },
    flags: {
      ...NO_FLAGS,
      price_drop: {
        last_drop_at: "2026-09-14T09:12:00",
        max_drop_percent: 12.5,
        variant_count: 1,
      },
    },
  },
  "SKU-BBB": {
    inventory: {
      known_quantity_total: 40,
      unknown_quantity_variants: 0,
      low_stock_variants: 0,
      sellable_variants: 2,
    },
    flags: { ...NO_FLAGS, new_since: "2026-09-10T00:00:00" },
  },
  "SKU-CCC": {
    inventory: {
      known_quantity_total: 12,
      unknown_quantity_variants: 0,
      low_stock_variants: 0,
      sellable_variants: 1,
    },
    flags: NO_FLAGS,
  },
  "SKU-DDD": {
    inventory: {
      known_quantity_total: 0,
      unknown_quantity_variants: 0,
      low_stock_variants: 0,
      sellable_variants: 3,
    },
    flags: NO_FLAGS,
    outOfStockVariants: 3,
  },
  "SKU-EEE": {
    inventory: {
      known_quantity_total: 0,
      unknown_quantity_variants: 2,
      low_stock_variants: 0,
      sellable_variants: 2,
    },
    flags: NO_FLAGS,
  },
};

export function resolveMockSearchProducts(
  siteId: string,
  params?: SearchProductsParams,
): SearchProductsResponse {
  const search = params?.search?.trim().toLowerCase();
  const productIds = params?.product_ids;

  let products = MOCK_PRODUCTS;
  if (productIds?.length) {
    const set = new Set(productIds);
    products = products.filter((p) => set.has(p.vendor_product_id));
  }
  if (search) {
    products = products.filter(
      (p) =>
        p.title.toLowerCase().includes(search) ||
        p.vendor_product_id.toLowerCase().includes(search) ||
        p.brand.toLowerCase().includes(search),
    );
  }
  return {
    products: products.map((p) => ({ ...p, site_id: siteId, site: siteId })),
    next: null,
  };
}

function buildMockVariantCounts(
  product: IngestedProductSchema,
): ProductCardVariantCounts {
  const variants = product.variants ?? [];
  const distinctByOption = new Map<string, Set<string>>();
  for (const variant of variants) {
    for (const option of variant.options ?? []) {
      if (!option.name) continue;
      const values = distinctByOption.get(option.name) ?? new Set<string>();
      if (option.value) values.add(option.value);
      distinctByOption.set(option.name, values);
    }
  }
  return {
    variants: variants.length,
    attributes: [...distinctByOption.entries()].map(([name, values]) => ({
      name,
      count: values.size,
      source: "options",
    })),
  };
}

function productToMockCard(product: IngestedProductSchema): ProductCard {
  return {
    product_id: product.vendor_product_id,
    title: product.title,
    image_url: product.assets?.find((a) => a.type === "image")?.url ?? "",
    price: {
      amount: product.price?.amount ?? "",
      sale: product.price?.sale ?? "",
      currency: product.price?.currency ?? "USD",
    },
    variant_counts: buildMockVariantCounts(product),
    inventory: MOCK_CARD_STATUS[product.vendor_product_id]?.inventory ?? null,
    flags: MOCK_CARD_STATUS[product.vendor_product_id]?.flags ?? null,
  };
}

function mockSortValue(
  product: IngestedProductSchema,
  sortBy: ProductCardSortBy,
): number {
  switch (sortBy) {
    case "newest":
      return Date.parse(product.published_at ?? "") || 0;
    case "low_stock": {
      // Mirrors the server: most stock problems first.
      const status = MOCK_CARD_STATUS[product.vendor_product_id];
      return (
        (status?.outOfStockVariants ?? 0) +
        (status?.inventory?.low_stock_variants ?? 0)
      );
    }
    case "score":
      // Mock products carry no score: keep catalog order.
      return 0;
    case "alphabetical":
      // Handled by the caller, which compares titles directly.
      return 0;
  }
}

export function resolveMockProductCards(
  siteId: string,
  productIds: string[],
): GetProductCardsResponse {
  const { products } = resolveMockSearchProducts(siteId, {
    product_ids: productIds,
  });
  return {
    products: products.map(productToMockCard),
  };
}

export function resolveMockListProductCards(
  params?: ListProductCardsParams,
): ListProductCardsResponse {
  const { collection, category, tag, sort_by } = params ?? {};
  let products = MOCK_PRODUCTS;
  if (collection) {
    products = products.filter((p) => p.collections.includes(collection));
  } else if (category) {
    products = products.filter((p) => p.categories.includes(category));
  } else if (tag) {
    products = products.filter((p) => p.tags.includes(tag));
  }
  if (sort_by === "alphabetical") {
    products = [...products].sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
    );
  } else if (sort_by) {
    products = [...products].sort(
      (a, b) => mockSortValue(b, sort_by) - mockSortValue(a, sort_by),
    );
  }
  const cards = products.map(productToMockCard);
  return {
    products: cards,
    count: cards.length,
    next: null,
  };
}

/** Aggregate a taxonomy field across the mock products into handle/count rows. */
function buildMockProductGroups(
  select: (product: IngestedProductSchema) => string[],
): CatalogProductGroup[] {
  const counts = new Map<string, number>();
  for (const product of MOCK_PRODUCTS) {
    for (const handle of select(product)) {
      counts.set(handle, (counts.get(handle) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([handle, product_count]) => ({ handle, product_count }))
    .sort((a, b) => a.handle.localeCompare(b.handle))
    .map((group, index) => ({ ...group, id: index + 1 }));
}

export function resolveMockListCollections(): ListCollectionsResponse {
  return {
    collections: buildMockProductGroups((p) => p.collections),
    next: null,
  };
}

export function resolveMockListCategories(): ListCategoriesResponse {
  return {
    categories: buildMockProductGroups((p) => p.categories),
    next: null,
  };
}

export function resolveMockListTags(): ListTagsResponse {
  return {
    tags: buildMockProductGroups((p) => p.tags),
    next: null,
  };
}

export function resolveMockSampleProducts(
  siteId: string,
  sampleLimit = 3,
): SearchProductsResponse {
  return {
    products: MOCK_PRODUCTS.slice(0, sampleLimit).map((p) => ({
      ...p,
      site_id: siteId,
      site: siteId,
    })),
    next: null,
  };
}

function filterHandles(
  handles: string[],
  q: string,
): CatalogHandleSearchResponse {
  const search = q.trim().toLowerCase();
  return {
    results: [...new Set(handles)]
      .filter((handle) => handle.toLowerCase().includes(search))
      .sort()
      .slice(0, 20),
  };
}

export function resolveMockSearchCategories(
  q: string,
): CatalogHandleSearchResponse {
  return filterHandles(
    MOCK_PRODUCTS.flatMap((p) => p.categories),
    q,
  );
}

export function resolveMockSearchCollections(
  q: string,
): CatalogHandleSearchResponse {
  return filterHandles(
    MOCK_PRODUCTS.flatMap((p) => p.collections),
    q,
  );
}

export function resolveMockSearchTags(q: string): CatalogHandleSearchResponse {
  return filterHandles(
    MOCK_PRODUCTS.flatMap((p) => p.tags),
    q,
  );
}

export function resolveMockSearchBrands(
  q: string,
): CatalogHandleSearchResponse {
  return filterHandles(
    MOCK_PRODUCTS.map((p) => p.brand),
    q,
  );
}

export function resolveMockSearchCatalog(
  siteId: string,
  q: string,
): CatalogSearchResponse {
  const search = q.trim().toLowerCase();
  const products = MOCK_PRODUCTS.filter(
    (p) =>
      p.title.toLowerCase().includes(search) ||
      p.vendor_product_id.toLowerCase().includes(search) ||
      p.sku.toLowerCase().includes(search),
  );
  return {
    results: products.map((p) => ({
      ...p,
      site_id: siteId,
      site: siteId,
      skus: p.sku ? [p.sku] : [],
    })),
  };
}
